/**
 * Tulia Favorites — guest list in localStorage, signed-in list in the customer metafield
 * WS8
 *
 * Guests: pipe-delimited handles in localStorage['tulia_favorites']
 * e.g. "the-ivy-top|the-kiki-top|the-bo-top"
 *
 * Signed in: the customer's custom.wishlist metafield (rendered into
 * window.__tulia.wishlistMap by theme.liquid) is the source of truth.
 * - Each heart tap becomes a change saved through the /apps/wishlist proxy
 * - Changes are sent one at a time, across all tabs: the worker reads, edits and
 *   rewrites the whole list, so overlapping saves would overwrite each other
 * - A change stays in localStorage['tulia_favorites_pending_<customer id>'] until the
 *   server confirms it, so one cut off by leaving the page (or by a network failure)
 *   is shown and resent on the next page load
 * - Favorites saved as a guest are merged into the account once on sign-in, then
 *   cleared from the browser — signing out hides the customer's favorites
 *
 * Uses event delegation to survive AJAX filter/sort reloads.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'tulia_favorites';
  var PENDING_KEY = 'tulia_favorites_pending_';
  var SEND_LOCK = 'tulia-favorites-send';
  var BTN_SELECTOR = '[data-wishlist-handle]';
  var ACTIVE_CLASS = 'is-active';
  var BADGE_SELECTOR = '.tulia-favorites-badge';
  var PROXY_BASE = '/apps/wishlist';
  var MAX_TRIES = 5; // page loads a change may fail to send on before it's given up and undone

  /* ---- Signed-in state ---- */
  var _customerId = null;
  var _accountFavorites = []; // account list with unsaved changes applied
  var _handleIdMap = {};      // { handle: numericId }
  var _pending = null;        // the change queue, kept here once localStorage refuses a write
  var _sending = false;
  var _paused = false;        // a send failed — wait for the next page load to try again

  /* ---- localStorage helpers (try-catch for Safari private mode) ---- */

  function readStorage(key) {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  /** Returns false when the browser refuses the write (storage full or blocked). */
  function writeStorage(key, value) {
    try {
      if (value) localStorage.setItem(key, value);
      else localStorage.removeItem(key);
      return true;
    } catch (e) {
      return false;
    }
  }

  function getGuestFavorites() {
    var raw = readStorage(STORAGE_KEY);
    return raw ? raw.split('|').filter(Boolean) : [];
  }

  function setGuestFavorites(arr) {
    writeStorage(STORAGE_KEY, arr.join('|'));
  }

  function isValidChange(op) {
    if (!op || typeof op !== 'object' || typeof op.id !== 'string') return false;
    if (op.action === 'merge') return Array.isArray(op.productIds) && Array.isArray(op.handles);
    return (op.action === 'add' || op.action === 'remove') &&
      typeof op.handle === 'string' && !!op.productId;
  }

  function getPending() {
    if (_pending) return _pending.slice();
    try {
      var ops = JSON.parse(readStorage(PENDING_KEY + _customerId) || '[]');
      return Array.isArray(ops) ? ops.filter(isValidChange) : [];
    } catch (e) {
      return [];
    }
  }

  function setPending(ops) {
    if (!_pending && writeStorage(PENDING_KEY + _customerId, ops.length ? JSON.stringify(ops) : '')) return;
    _pending = ops.slice(); // storage refused: keep the queue for this page instead
  }

  /* ---- Favorites list (guest storage, or the account list when signed in) ---- */

  function getFavorites() {
    return _customerId ? _accountFavorites.slice() : getGuestFavorites();
  }

  function setFavorites(arr) {
    if (_customerId) _accountFavorites = arr;
    else setGuestFavorites(arr);
  }

  function isFavorited(handle) {
    return getFavorites().indexOf(handle) !== -1;
  }

  function addFavorite(handle) {
    var favs = getFavorites();
    if (favs.indexOf(handle) === -1) {
      favs.push(handle);
      setFavorites(favs);
    }
    return favs;
  }

  function removeFavorite(handle) {
    var favs = getFavorites();
    var idx = favs.indexOf(handle);
    if (idx !== -1) {
      favs.splice(idx, 1);
      setFavorites(favs);
    }
    return favs;
  }

  /* ---- UI helpers ---- */

  function syncButton(btn) {
    var handle = btn.getAttribute('data-wishlist-handle');
    if (!handle) return;
    var active = isFavorited(handle);
    btn.classList.toggle(ACTIVE_CLASS, active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    btn.setAttribute('aria-label',
      active ? 'Remove from favorites' : 'Add to favorites'
    );
  }

  function syncAllButtons() {
    var buttons = document.querySelectorAll(BTN_SELECTOR);
    for (var i = 0; i < buttons.length; i++) {
      syncButton(buttons[i]);
    }
  }

  function updateBadge() {
    var count = getFavorites().length;
    var badges = document.querySelectorAll(BADGE_SELECTOR);
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = count;
      badges[i].style.display = count > 0 ? '' : 'none';
    }
  }

  function refreshUI() {
    syncAllButtons();
    updateBadge();
  }

  /* ---- Product ID helpers ---- */

  function getProductId(btn) {
    var id = btn.getAttribute('data-product-id');
    return id ? parseInt(id, 10) : null;
  }

  /**
   * Look up product IDs for handles via /products/{handle}.js.
   * Resolves { found: { handle: { id, handle } }, gone: [handles that 404] }.
   * A handle that fails for any other reason (network, server) is in neither —
   * the product may still exist, so callers keep it for a later try.
   */
  function resolveHandleIds(handles) {
    var lookups = handles.map(function (handle) {
      return fetch('/products/' + handle + '.js')
        .then(function (r) {
          if (r.status === 404) return { handle: handle, gone: true };
          if (!r.ok) return null;
          return r.json().then(function (p) {
            return { handle: handle, product: { id: p.id, handle: p.handle } };
          });
        })
        .catch(function () { return null; });
    });
    return Promise.all(lookups).then(function (results) {
      var out = { found: {}, gone: [] };
      results.filter(Boolean).forEach(function (r) {
        if (r.gone) out.gone.push(r.handle);
        else out.found[r.handle] = r.product;
      });
      return out;
    });
  }

  /* ---- Saving changes to the account ---- */

  /** Record a change and start sending. A tap that undoes a never-sent change cancels it. */
  function queueChange(op) {
    var ops = getPending();
    op.id = Date.now() + '-' + Math.random().toString(36).slice(2, 8);

    if (op.action !== 'merge') {
      for (var i = ops.length - 1; i >= 0; i--) {
        if (ops[i].handle !== op.handle) continue;
        if (ops[i].action === op.action) return;
        if (!ops[i].tries) {
          ops.splice(i, 1);
          setPending(ops);
          return;
        }
        break; // it may already have reached the server, so send the undo too
      }
    }
    ops.push(op);
    setPending(ops);
    sendNext();
  }

  function sendChange(op) {
    var merge = op.action === 'merge';
    return fetch(PROXY_BASE + (merge ? '/merge' : '/toggle'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(merge
        ? { productIds: op.productIds }
        : { productId: op.productId, action: op.action }),
      keepalive: true, // let a save started just before leaving the page finish
    }).then(function (r) {
      if (r.ok) return 'saved';
      if (r.status === 408 || r.status === 429 || r.status >= 500) return 'retry';
      return 'rejected';
    }).catch(function () {
      return 'retry';
    });
  }

  /** Put a change the server won't take back the way it was before the tap. */
  function undoChange(op) {
    if (op.action === 'add') {
      removeFavorite(op.handle);
    } else if (op.action === 'remove') {
      addFavorite(op.handle);
    } else {
      // merge: keep them as guest favorites so nothing is lost
      var guest = getGuestFavorites();
      setGuestFavorites(guest.concat(op.handles.filter(function (h) {
        return guest.indexOf(h) === -1;
      })));
    }
    refreshUI();
  }

  function removePending(id) {
    setPending(getPending().filter(function (o) { return o.id !== id; }));
  }

  /** Send queued changes oldest first, one at a time, until none are left or one fails. */
  function sendQueue() {
    var ops = getPending();
    if (!ops.length || _paused) return Promise.resolve();
    var op = ops[0];
    op.tries = (op.tries || 0) + 1;
    setPending(ops); // record the attempt first, so a later tap can't cancel it

    return sendChange(op).then(function (result) {
      if (result === 'retry') {
        if (op.tries < MAX_TRIES) {
          _paused = true; // keep it (and everything after it) for the next page load
          return;
        }
        result = 'rejected';
      }
      removePending(op.id);
      if (result === 'rejected') undoChange(op);
      if (!getPending().length) document.dispatchEvent(new CustomEvent('tulia:favorites:saved'));
      return sendQueue();
    });
  }

  function sendNext() {
    if (_sending || _paused || !getPending().length) return;
    _sending = true;
    var done = function () {
      _sending = false;
      sendNext(); // a change queued while the last one was finishing
    };
    // One sender across tabs (they share the queue); older browsers fall back to this tab only
    if (navigator.locks && navigator.locks.request) {
      navigator.locks.request(SEND_LOCK, sendQueue).then(done, done);
    } else {
      sendQueue().then(done, done);
    }
  }

  /** The account list as the page rendered it, with this browser's unsent changes applied. */
  function loadAccountFavorites() {
    var map = window.__tulia.wishlistMap || {};
    var handles = Object.keys(map);
    handles.forEach(function (h) { _handleIdMap[h] = map[h]; });

    getPending().forEach(function (op) {
      if (op.action === 'merge') {
        op.handles.forEach(function (h) {
          if (handles.indexOf(h) === -1) handles.push(h);
        });
        return;
      }
      _handleIdMap[op.handle] = op.productId;
      var idx = handles.indexOf(op.handle);
      if (op.action === 'add' && idx === -1) handles.push(op.handle);
      if (op.action === 'remove' && idx !== -1) handles.splice(idx, 1);
    });
    return handles;
  }

  /**
   * Merge favorites saved as a guest into the account, then clear them from the
   * browser. Ones that couldn't be looked up (network trouble) stay for the next load.
   */
  function mergeGuestFavorites() {
    var guest = getGuestFavorites();
    if (!guest.length) return;

    guest.forEach(addFavorite); // show them straight away

    resolveHandleIds(guest).then(function (res) {
      // Un-hearted while we were looking them up: drop them
      guest = guest.filter(isFavorited);

      var ids = [];
      var merged = [];
      guest.forEach(function (h) {
        var p = res.found[h];
        if (!p) return;
        ids.push(p.id);
        merged.push(p.handle);
        _handleIdMap[p.handle] = p.id;
        if (p.handle !== h) { // product handle was renamed
          removeFavorite(h);
          addFavorite(p.handle);
        }
      });
      res.gone.forEach(removeFavorite);

      setGuestFavorites(guest.filter(function (h) {
        return !res.found[h] && res.gone.indexOf(h) === -1;
      }));
      if (ids.length) queueChange({ action: 'merge', productIds: ids, handles: merged });
      refreshUI();
    });
  }

  /* ---- Click handler ---- */

  function handleClick(e) {
    var btn = e.target.closest(BTN_SELECTOR);
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    var handle = btn.getAttribute('data-wishlist-handle');
    if (!handle) return;

    var wasActive = btn.classList.contains(ACTIVE_CLASS);
    var action = wasActive ? 'remove' : 'add';

    // Optimistic local update
    if (wasActive) {
      removeFavorite(handle);
    } else {
      addFavorite(handle);
    }

    refreshUI();

    var productId = getProductId(btn) || _handleIdMap[handle];

    document.dispatchEvent(new CustomEvent('tulia:favorites:changed', {
      detail: { handle: handle, action: action, productId: productId, button: btn }
    }));

    // Save to the account for signed-in customers
    if (_customerId && productId) {
      _handleIdMap[handle] = productId;
      queueChange({ productId: productId, handle: handle, action: action });
    }
  }

  /* ---- Init ---- */

  function init() {
    document.addEventListener('click', handleClick);

    if (window.__tulia && window.__tulia.customerId) {
      _customerId = window.__tulia.customerId;
      _accountFavorites = loadAccountFavorites();
      mergeGuestFavorites();
    }

    refreshUI();
    if (_customerId) sendNext();
  }

  window.TuliaFavorites = {
    get: getFavorites,
    add: addFavorite,
    remove: removeFavorite,
    isFavorited: isFavorited,
    isLoggedIn: function () { return !!_customerId; },
    isSaving: function () { return !!_customerId && getPending().length > 0; }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
